import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../components/css/AdminProjectCustomers.css";

axios.defaults.withCredentials = true;

const ITEMS_PER_PAGE = 30;

const AdminProjectCustomersPage = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [expiryFilter, setExpiryFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  useEffect(() => {
    fetchProjectCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    selectedCompany,
    selectedCustomer,
    selectedStatus,
    expiryFilter,
  ]);

  const companyOptions = useMemo(() => {
    return [
      ...new Set(
        rows
          .map((item) => item.cus_company)
          .filter((value) => value && String(value).trim() !== ""),
      ),
    ];
  }, [rows]);

  const customerOptions = useMemo(() => {
    return [
      ...new Set(
        rows
          .map((item) => item.customer_name)
          .filter((value) => value && String(value).trim() !== ""),
      ),
    ];
  }, [rows]);

  useEffect(() => {
    const keyword = search.toLowerCase().trim();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const next60Days = new Date(today);
    next60Days.setDate(today.getDate() + 60);

    const filtered = rows.filter((item) => {
      const searchText = [
        item.prd_serial,
        item.prd_label,
        item.customer_name,
        item.cus_company,
        item.ma_status,
        item.expiry,
        item.current_end_date,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchSearch = !keyword || searchText.includes(keyword);

      const matchCompany =
        !selectedCompany || item.cus_company === selectedCompany;

      const matchCustomer =
        !selectedCustomer || item.customer_name === selectedCustomer;

      const matchStatus =
        !selectedStatus || item.ma_status === selectedStatus;

      let matchExpiry = true;

      if (expiryFilter === "within_60_days") {
        if (!item.current_end_date_raw) {
          matchExpiry = false;
        } else {
          const expiryDate = new Date(item.current_end_date_raw);
          expiryDate.setHours(0, 0, 0, 0);

          matchExpiry = expiryDate >= today && expiryDate <= next60Days;
        }
      }

      return (
        matchSearch &&
        matchCompany &&
        matchCustomer &&
        matchStatus &&
        matchExpiry
      );
    });

    setFilteredRows(filtered);
  }, [
    search,
    selectedCompany,
    selectedCustomer,
    selectedStatus,
    expiryFilter,
    rows,
  ]);

  const handleSort = (key) => {
    setSortConfig((previousSortConfig) => {
      if (previousSortConfig.key === key) {
        return {
          key,
          direction:
            previousSortConfig.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });

    setCurrentPage(1);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }

    return (
      <span
        style={{
          marginLeft: "5px",
          fontSize: "9px",
          verticalAlign: "middle",
        }}
      >
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) {
      return [...filteredRows];
    }

    return [...filteredRows].sort((firstItem, secondItem) => {
      let firstValue = firstItem?.[sortConfig.key];
      let secondValue = secondItem?.[sortConfig.key];

      const firstIsEmpty =
        firstValue === null ||
        firstValue === undefined ||
        String(firstValue).trim() === "";

      const secondIsEmpty =
        secondValue === null ||
        secondValue === undefined ||
        String(secondValue).trim() === "";

      if (firstIsEmpty && secondIsEmpty) {
        return 0;
      }

      if (firstIsEmpty) {
        return 1;
      }

      if (secondIsEmpty) {
        return -1;
      }

      if (sortConfig.key === "current_end_date_raw") {
        firstValue = new Date(firstValue).getTime();
        secondValue = new Date(secondValue).getTime();

        const compareResult = firstValue - secondValue;

        return sortConfig.direction === "asc"
          ? compareResult
          : -compareResult;
      }

      const compareResult = String(firstValue).localeCompare(
        String(secondValue),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      );

      return sortConfig.direction === "asc"
        ? compareResult
        : -compareResult;
    });
  }, [filteredRows, sortConfig]);

  const totalPages = Math.ceil(sortedRows.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

  const paginatedRows = sortedRows.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const fetchProjectCustomers = async () => {
    try {
      const res = await axios.get(
        "/project-customers",
        {
          withCredentials: true,
        },
      );

      const data = Array.isArray(res.data) ? res.data : [];

      setRows(data);
      setFilteredRows(data);
    } catch (err) {
      console.error("fetchProjectCustomers error:", err);
      setRows([]);
      setFilteredRows([]);
    }
  };

  const handleOpenDetail = (item) => {
    navigate(`/admin/customer-assets/${item.id}`);
  };

  const handleClearFilters = () => {
    setSearch("");
    setSelectedCompany("");
    setSelectedCustomer("");
    setSelectedStatus("");
    setExpiryFilter("");
  };

  return (
    <div className="apc-page">
      <div className="apc-card">
        <div className="apc-header">
          <div className="apc-header-top">
            <h2 className="apc-title">Customer Assets</h2>
          </div>

          <div className="apc-filters">
            <select
              className="apc-filter-select"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
            >
              <option value="">All Company</option>

              {companyOptions.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>

            <select
              className="apc-filter-select"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value="">All Customer</option>

              {customerOptions.map((customer) => (
                <option key={customer} value={customer}>
                  {customer}
                </option>
              ))}
            </select>

            <select
              className="apc-filter-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Expired">Expired</option>
            </select>

            <select
              className="apc-filter-select"
              value={expiryFilter}
              onChange={(e) => setExpiryFilter(e.target.value)}
            >
              <option value="">All Expiry</option>
              <option value="within_60_days">
                Expiring within 60 days
              </option>
            </select>
          </div>

          <div className="apc-search-group">
            <input
              className="apc-search"
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <button
              type="button"
              className="apc-clear-filter"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>

          <div className="apc-filter-info-wrap">
            <p>*ระบบกรองเฉพาะ user ที่มีข้อมูล asset เท่านั้น</p>
          </div>
        </div>

        <div className="apc-table-wrap">
          <table className="apc-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("prd_serial")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Serial Number
                  {renderSortIcon("prd_serial")}
                </th>

                <th
                  onClick={() => handleSort("prd_label")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Product
                  {renderSortIcon("prd_label")}
                </th>

                <th
                  onClick={() => handleSort("customer_name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Customer
                  {renderSortIcon("customer_name")}
                </th>

                <th
                  onClick={() => handleSort("cus_company")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Company
                  {renderSortIcon("cus_company")}
                </th>

                <th
                  onClick={() => handleSort("current_end_date_raw")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Expiry Date
                  {renderSortIcon("current_end_date_raw")}
                </th>

                <th
                  onClick={() => handleSort("ma_status")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Status
                  {renderSortIcon("ma_status")}
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleOpenDetail(item)}
                  >
                    <td className="serial-soft-link">
                      {item.prd_serial || "-"}
                    </td>

                    <td>{item.prd_label || "-"}</td>
                    <td>{item.customer_name || "-"}</td>
                    <td>{item.cus_company || "-"}</td>
                    <td>{item.current_end_date || "-"}</td>

                    <td>
                      <span
                        className={
                          item.ma_status === "Active"
                            ? "apc-badge apc-badge-active"
                            : "apc-badge apc-badge-expired"
                        }
                      >
                        {item.ma_status || "-"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="apc-empty">
                    ไม่พบข้อมูล
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="ticket-pagination">
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.max(prev - 1, 1))
              }
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={currentPage === page ? "active-page" : ""}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(prev + 1, totalPages),
                )
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProjectCustomersPage;
